
import React, { useState, useEffect } from "react";
import { useSiteSettings } from '../../context/SiteSettingsContext';
import ModalWrapper from '../../components/Modal/ModalWrapper';
import { useAuth } from "../../hooks/useAuth";
import { useUser } from "../../context/UserContext";
import type { User } from "../../context/UserContext";

const ManageProfile: React.FC = () => {
  const { settings: siteSettings, loading: isSiteSettingsLoading } = useSiteSettings();
  const logLevel = siteSettings?.frontend_log_level ?? 0;
const { user, loading, refreshUser, updateUser } = useUser();
  const [form, setForm] = useState<any>({});
  useEffect(() => {
    if (user) {
      setForm(user);
    }
  }, [user]);
  const [changed, setChanged] = useState(false);
  const [showEmailError, setShowEmailError] = useState(false);
  const [showNameError, setShowNameError] = useState(false);
  const [showAccountDeleteDialog, setShowAccountDeleteDialog] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const { deleteAccount } = useAuth();
  const handleDeleteAccount = async () => {
    try {
      await deleteAccount();
    } catch (err) {
      if (!isSiteSettingsLoading && logLevel >= 1) {
        console.error("Account-Löschung fehlgeschlagen:", err);
      }
    }
  };


  const salutationOptions = [
    { value: "mr", label: "Mr." },
    { value: "ms", label: "Ms." },
    { value: "mx", label: "Mx." },
    { value: "dr", label: "Dr." },
  ];

  const titleOptions = [
    { value: "therapist", label: "Therapeut/ in" },
    { value: "psychotherapist", label: "Psychotherapeut/ in" },
    { value: "counselor", label: "Berater/ in" },
    { value: "trainer", label: "Trainer/ in" },
  ];




  const validateEmail = (value: string) => {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);
  };

  const handleChange = (field: string, value: string) => {
    setForm((prev: any) => {
      const updated = { ...prev, [field]: value };
      setChanged(JSON.stringify(updated) !== JSON.stringify(user));
      if (field === "email") {
        setShowEmailError(!validateEmail(value));
      }
      if (field === "name") {
        setShowNameError(!value.trim());
      }
      return updated;
    });
  };

  const handleSave = async () => {
    let abort = false;
    if (!form.name?.trim()) {
      setShowNameError(true);
      abort = true;
    } else {
      setShowNameError(false);
    }
    if (!validateEmail(form.email)) {
      setShowEmailError(true);
      abort = true;
    } else {
      setShowEmailError(false);
    }
    if (abort) return;
    const patchPayload: Partial<User> = {};
    (Object.keys(form) as (keyof User)[]).forEach((key) => {
      if (form[key] !== user?.[key]) {
        patchPayload[key] = form[key];
      }
    });
    await updateUser(patchPayload);
    setChanged(false);
  };


  return (
    <div className="tw-w-full tw-pt-6  tw-pb-32 sm:tw-pb-0 tw-overflow-y-scroll scroll-hidden">
      <div className="profile-section tw-pb-4 tw-border-0 tw-w-full tw-border-b-2 tw-border-solid tw-border-[#EBEBEB] tw-space-y-4">
        <form>
          <div className="tw-flex tw-justify-between tw-items-center">
            <h3 className="tw-text-[20px] tw-font-semibold tw-my-0">Persönliche Daten</h3>
            <div className="sm:tw-flex tw-fixed tw-z-10 sm:tw-relative tw-justify-center tw-w-screen sm:tw-w-fit !tw-bg-white tw-bottom-0 tw-left-0 tw-py-4 sm:tw-py-0 mobile-cta">
              <button
                className="_button_19rto_40 _purple_19rto_126 _filled_19rto_80 disabled:!tw-opacity-60 tw-cursor-pointer tw-px-10 tw-w-full sm:tw-w-fit"

                type="button"
                onClick={handleSave}
                disabled={!changed}

                id=""


              >
                <div className="_btnFlex_19rto_32">
                  <p className="tw-m-0 tw-block tw-truncate !tw-text-base">
                    Speichern
                  </p>
                </div>
              </button>
            </div>
          </div>

          <div className="tw-flex tw-flex-col tw-space-y-4 tw-mt-4">
            <div className="input__container" style={{ pointerEvents: "auto", opacity: 1 }}>
              <label htmlFor="name" className="input__container__label !tw-text-[12px] !tw-text-gray-600 !tw-font-normal">
                Name
                <span className="tw-text-danger-500">*</span>
              </label>
              <div className="input__container__div ad-input__container">
                <input
                  className="input__container__div__input !tw-bg-white !tw-text-gray-600"
                  type="text"
                  name="name"
                  id="name"
                  placeholder="Bitte geben Sie Ihren Namen ein"
                  maxLength={40}
                  inputMode="text"
                  value={form?.name || ""}
                  onChange={(e) => handleChange("name", e.target.value)}
                />
              </div>
              {showNameError && (
                <p className="input__container__div__error !tw-text-xs">
                  Dieses Feld darf nicht leer sein.
                </p>
              )}
            </div>

            <div className="input__container" style={{ pointerEvents: "auto", opacity: 1 }}>
              <label htmlFor="email" className="input__container__label !tw-text-[12px] !tw-text-gray-600 !tw-font-normal">
                Email
                <span className="tw-text-danger-500">*</span>
              </label>

              <div className="input__container__div ad-input__container">
                <input
                  className="input__container__div__input !tw-bg-white !tw-text-gray-600"
                  type="email"
                  name="email"
                  id="email"
                  placeholder="Bitte geben Sie Ihre E-Mail-Adresse ein"
                  inputMode="text"
                  value={form?.email || ""}
                  onChange={(e) => handleChange("email", e.target.value)}
                />
              </div>
              {showEmailError && (
                <p className="input__container__div__error !tw-text-xs">
                  Ungültiges E-Mail-Format. Bitte geben Sie Ihre E-Mail-Adresse in diesem Format ein: name@beispiel.de
                </p>)}
            </div>

            <div className="tw-flex tw-flex-col md:tw-flex-row md:tw-items-start tw-justify-between tw-gap-3">
              <div>
                <p className="tw-m-0 tw-text-[12px] tw-font-normal">Pronomen</p>
                <div className="radio__group__each !tw-gap-6 tw-flex-wrap">
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <input
                      type="radio"
                      id="male"
                      name="gender"
                      className="lumanoteEX__radio tw-cursor-pointer"
                      value="male"
                      checked={form.gender === "male"}
                      onChange={(e) => handleChange("gender", e.target.value)}
                    />
                    <label htmlFor="male" className="radio__group__each__label">Er/Ihm</label>
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <input
                      type="radio"
                      id="female"
                      name="gender"
                      className="lumanoteEX__radio tw-cursor-pointer"
                      value="female"
                      checked={form.gender === "female"}
                      onChange={(e) => handleChange("gender", e.target.value)}
                    />
                    <label htmlFor="female" className="radio__group__each__label">Er/Sie</label>
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <input
                      type="radio"
                      id="other"
                      name="gender"
                      className="lumanoteEX__radio tw-cursor-pointer"
                      value="other"
                      checked={form.gender === "other"}
                      onChange={(e) => handleChange("gender", e.target.value)}
                    />
                    <label htmlFor="other" className="radio__group__each__label">Divers</label>
                  </div>
                </div>
              </div>

              <div className="tw-flex tw-flex-col tw-flex-1 md:tw-flex-initial md:tw-w-[40%] lg:tw-w-[50%] tw-gap-y-3">
                <div className="select-container tw-flex-1" id="">
                  <div>
                    <label className="">
                      <span className="tw-text-xs tw-font-normal">
                        Wähle Sie die Option, die Sie am besten bescheibt

                      </span>
                    </label>
                  </div>
                  <div className="css-b62m3t-container">
                    <span id="react-select-4-live-region" className="css-7pg0cj-a11yText"></span>
                    <span
                      aria-live="polite"
                      aria-atomic="false"
                      aria-relevant="additions text"
                      role="log"
                      className="css-7pg0cj-a11yText"
                    ></span>
                    <div className="css-m8vlz-control">
                      <div className="css-1fohiws">
                        <div className="css-19bb58m" data-value="">
                          <select
                            className="input__container__div__input !tw-bg-white !tw-text-gray-600"
                            value={form.title}
                            onChange={(e) => handleChange("title", e.target.value)}
                          >
                            {titleOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div><div className="tw-pt-4">
        <h3 className="tw-text-[14px] tw-font-normal tw-text-gray-900">Account löschen</h3>
        <div className="xl:tw-flex tw-items-center"><p className="tw-text-[14px] tw-text-gray-500 tw-mb-0">Das Löschen Ihres Kontos führt zum endgültigen Verlust aller auf LumaNote gespeicherten Daten, Dokumentationen, Vorlagen und aller weiteren Informationen, die während Ihrer Nutzung der Plattform erfasst wurden. Bitte beachten Sie, dass dieser Vorgang nicht rückgängig gemacht werden kann und eine Wiederherstellung Ihrer Daten nicht möglich ist.</p
        ><button className=" pl-4 tw-flex tw-m-auto tw-mt-4 xl:tw-m-0 tw-space-x-2 lg:tw-w-60 tw-border-0 tw-bg-transparent tw-items-center tw-cursor-pointer"
          onClick={() => {
            setDeleteConfirmed(false);
            setShowAccountDeleteDialog(true);
            }}>

            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.9946 4.48242C13.9812 4.48242 13.9612 4.48242 13.9412 4.48242C10.4146 4.12909 6.89457 3.99575 3.40791 4.34909L2.04791 4.48242C1.76791 4.50909 1.52124 4.30909 1.49457 4.02909C1.46791 3.74909 1.66791 3.50909 1.94124 3.48242L3.30124 3.34909C6.84791 2.98909 10.4412 3.12909 14.0412 3.48242C14.3146 3.50909 14.5146 3.75575 14.4879 4.02909C14.4679 4.28909 14.2479 4.48242 13.9946 4.48242Z" fill="#F3404F"></path><path d="M5.66456 3.8091C5.63789 3.8091 5.61122 3.8091 5.57789 3.80243C5.31122 3.75577 5.12456 3.49577 5.17122 3.2291L5.31789 2.35577C5.42456 1.71577 5.57122 0.829102 7.12456 0.829102H8.87122C10.4312 0.829102 10.5779 1.7491 10.6779 2.36243L10.8246 3.2291C10.8712 3.50243 10.6846 3.76243 10.4179 3.80243C10.1446 3.8491 9.88456 3.66243 9.84456 3.39577L9.69789 2.5291C9.60456 1.9491 9.58456 1.83577 8.87789 1.83577H7.13122C6.42456 1.83577 6.41122 1.9291 6.31122 2.52243L6.15789 3.3891C6.11789 3.63577 5.90456 3.8091 5.66456 3.8091Z" fill="#F3404F"></path><path d="M10.1377 15.1628H5.85765C3.53099 15.1628 3.43765 13.8761 3.36432 12.8361L2.93099 6.12281C2.91099 5.84948 3.12432 5.60948 3.39765 5.58948C3.67765 5.57614 3.91099 5.78281 3.93099 6.05614L4.36432 12.7695C4.43765 13.7828 4.46432 14.1628 5.85765 14.1628H10.1377C11.5377 14.1628 11.5643 13.7828 11.631 12.7695L12.0643 6.05614C12.0843 5.78281 12.3243 5.57614 12.5977 5.58948C12.871 5.60948 13.0843 5.84281 13.0643 6.12281L12.631 12.8361C12.5577 13.8761 12.4643 15.1628 10.1377 15.1628Z" fill="#F3404F"></path><path d="M9.10281 11.4961H6.88281C6.60948 11.4961 6.38281 11.2694 6.38281 10.9961C6.38281 10.7228 6.60948 10.4961 6.88281 10.4961H9.10281C9.37615 10.4961 9.60281 10.7228 9.60281 10.9961C9.60281 11.2694 9.37615 11.4961 9.10281 11.4961Z" fill="#F3404F"></path><path d="M9.66146 8.8291H6.32812C6.05479 8.8291 5.82812 8.60244 5.82812 8.3291C5.82812 8.05577 6.05479 7.8291 6.32812 7.8291H9.66146C9.93479 7.8291 10.1615 8.05577 10.1615 8.3291C10.1615 8.60244 9.93479 8.8291 9.66146 8.8291Z" fill="#F3404F"></path></svg><p className="tw-text-nowrap tw-m-0  tw-text-base tw-text-danger-500">
              Account löschen</p></button></div></div>
      {showAccountDeleteDialog && (
        <ModalWrapper
        onClose={() => {
          setDeleteConfirmed(false);
          setShowAccountDeleteDialog(false);
         }} 
         title="Bestätigung der Löschung Ihres Accounts">
          <div>
            <p className="tw-text-sm tw-leading-[24px] tw-text-gray-500 text-center">
              Das Löschen Ihres Kontos führt zum endgültigen Verlust aller auf LumaNote gespeicherten Daten, einschließlich Dokumentationen, Sitzungen, Vorlagen und aller weiteren Informationen, die während Ihrer Nutzung der Plattform erfasst wurden.
            </p>
            <p className="tw-text-center tw-text-sm tw-mx-auto tw-text-gray-500">
              Bitte beachten Sie, dass dieser Vorgang nicht rückgängig gemacht werden kann und eine Wiederherstellung Ihrer Daten zu einem späteren Zeitpunkt nicht möglich ist.
            </p>
            <div className="tw-flex tw-grow md:tw-grow-0 tw-flex-col tw-mb-20 md:tw-mb-0">
              <label className="tw-inline-flex tw-space-x-3 tw tw-cursor-pointer tw-text-xs sm:tw-text-sm lg:tw-text-base tw-my-3 sm:tw-my-5 tw-items-start">
                <div className="tw-inline-block">
                  <input
                    required
                    className="tw-h-4 tw-w-4 tw-border tw-border-gray-300 tw-rounded tw-cursor-pointer checked:bg-[#40937a]"
                    type="checkbox"
                    value="agree"
                    checked={deleteConfirmed}
                    onChange={() => setDeleteConfirmed(!deleteConfirmed)}
                  />
                </div>
                <p className="-tw-m-1 tw-text-gray-500 tw-text-sm">
                  Ich bestätige, dass ich mein Konto dauerhaft löschen möchte und mir bewusst ist, dass alle meine Daten unwiderruflich entfernt werden.
                </p>
                
              </label>

            </div>
            {deleteConfirmed && (
            <p className="-tw-m-1 tw-text-gray-500 tw-text-sm text-center">
              Nach dem Löschen werden Sie automatisch ausgeloggt.</p>)}
            <div className="tw-fixed md:tw-sticky md:before:tw-content-['*'] md:before:tw-bg-white md:before:tw-w-[50rem] md:before:tw-h-[5.5rem] md:before:tw-absolute md:before:-tw-bottom-[1.5rem] md:before:-tw-left-1/4 md:before:-tw-z-10 tw-bottom-0 tw-z-20 tw-left-0 tw-py-4 !tw-px-3 tw-shadow-[4px_4px_16px_0px_#0000001A] md:tw-shadow-none tw-w-full md:tw-grow-0 tw-flex-col md:tw-mt-10 md:tw-py-0 md:!tw-px-0 tw-bg-white modal-footer md:tw-flex tw-justify-end">
              <button
                disabled={!deleteConfirmed}
                onClick={handleDeleteAccount}
                className={`tw-inline-flex tw-w-full md:tw-w-fit tw-px-3 tw-py-2 tw-ml-auto tw-mt-2 tw-rounded tw-shadow tw-space-x-2 tw-border-0 tw-items-center tw-justify-center md:tw-justify-start ${
                  !deleteConfirmed
                    ? 'tw-bg-danger-500/60 text-white tw-cursor-not-allowed'
                    : 'tw-bg-danger-500 text-white tw-cursor-pointer'
                }`}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="!tw-fill-white tw-text-white"><path d="M13.9965 4.48242C13.9832 4.48242 13.9632 4.48242 13.9432 4.48242C10.4165 4.12909 6.89653 3.99575 3.40986 4.34909L2.04986 4.48242C1.76986 4.50909 1.52319 4.30909 1.49653 4.02909C1.46986 3.74909 1.66986 3.50909 1.94319 3.48242L3.30319 3.34909C6.84986 2.98909 10.4432 3.12909 14.0432 3.48242C14.3165 3.50909 14.5165 3.75575 14.4899 4.02909C14.4699 4.28909 14.2499 4.48242 13.9965 4.48242Z" fill="white"></path><path d="M5.6626 3.8091C5.63594 3.8091 5.60927 3.8091 5.57594 3.80243C5.30927 3.75577 5.1226 3.49577 5.16927 3.2291L5.31594 2.35577C5.4226 1.71577 5.56927 0.829102 7.1226 0.829102H8.86927C10.4293 0.829102 10.5759 1.7491 10.6759 2.36243L10.8226 3.2291C10.8693 3.50243 10.6826 3.76243 10.4159 3.80243C10.1426 3.8491 9.8826 3.66243 9.8426 3.39577L9.69594 2.5291C9.6026 1.9491 9.5826 1.83577 8.87594 1.83577H7.12927C6.4226 1.83577 6.40927 1.9291 6.30927 2.52243L6.15594 3.3891C6.11594 3.63577 5.9026 3.8091 5.6626 3.8091Z" fill="white"></path><path d="M10.1357 15.1628H5.8557C3.52903 15.1628 3.4357 13.8761 3.36237 12.8361L2.92903 6.12281C2.90903 5.84948 3.12237 5.60948 3.3957 5.58948C3.6757 5.57614 3.90903 5.78281 3.92903 6.05614L4.36237 12.7695C4.4357 13.7828 4.46237 14.1628 5.8557 14.1628H10.1357C11.5357 14.1628 11.5624 13.7828 11.629 12.7695L12.0624 6.05614C12.0824 5.78281 12.3224 5.57614 12.5957 5.58948C12.869 5.60948 13.0824 5.84281 13.0624 6.12281L12.629 12.8361C12.5557 13.8761 12.4624 15.1628 10.1357 15.1628Z" fill="white"></path><path d="M9.10281 11.4961H6.88281C6.60948 11.4961 6.38281 11.2694 6.38281 10.9961C6.38281 10.7228 6.60948 10.4961 6.88281 10.4961H9.10281C9.37615 10.4961 9.60281 10.7228 9.60281 10.9961C9.60281 11.2694 9.37615 11.4961 9.10281 11.4961Z" fill="white"></path><path d="M9.66341 8.8291H6.33008C6.05674 8.8291 5.83008 8.60244 5.83008 8.3291C5.83008 8.05577 6.05674 7.8291 6.33008 7.8291H9.66341C9.93674 7.8291 10.1634 8.05577 10.1634 8.3291C10.1634 8.60244 9.93674 8.8291 9.66341 8.8291Z" fill="white"></path></svg>
                <p className="tw-text-nowrap tw-m-0 tw-text-base tw-text-white tw-font-normal">Meinen Account löschen</p>
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}
    </div>



  );
};

export default ManageProfile;